import { B } from "../constants";
import { useState, useEffect } from "react";

const mono = "IBM Plex Mono,monospace";
const sans = "IBM Plex Sans,sans-serif";

const sectionStyle = {
  marginBottom: 40,
};

const h2Style = {
  fontSize: 16,
  fontWeight: 600,
  color: B.accent,
  fontFamily: mono,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  marginBottom: 12,
};

const pStyle = {
  fontSize: 15,
  lineHeight: 1.7,
  color: B.textSec,
  marginBottom: 12,
};

const liStyle = {
  fontSize: 14,
  lineHeight: 1.7,
  color: B.textSec,
  marginBottom: 8,
  paddingLeft: 4,
};

const strongStyle = {
  color: B.text,
  fontWeight: 500,
};

const flowBoxStyle = {
  background: B.surface,
  border: `1px solid ${B.border}`,
  borderRadius: 8,
  padding: "20px 24px",
  fontFamily: mono,
  fontSize: 13,
  color: B.textSec,
  lineHeight: 2,
  overflowX: "auto",
};

const statCardStyle = {
  background: B.surface,
  border: `1px solid ${B.border}`,
  borderRadius: 8,
  padding: "20px 24px",
  textAlign: "center",
  flex: "1 1 180px",
  minWidth: 180,
};

export default function PoweredByClaude() {
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    fetch("/api/misc?action=powered-by-claude")
      .then(r => r.ok ? r.json() : null)
      .then(setMetrics)
      .catch((err) => console.error('[PoweredByClaude:useEffect] metrics fetch failed:', err));
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: B.bg, color: B.textSec, fontFamily: sans, padding: "40px 24px 80px", maxWidth: 720, margin: "0 auto" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500&display=swap');*{box-sizing:border-box;margin:0;padding:0}a:hover{opacity:0.85}`}</style>

      <a href="/" style={{ fontSize: 11, letterSpacing: "0.2em", color: B.accent, textTransform: "uppercase", fontWeight: 600, textDecoration: "none", fontFamily: mono }}>FYNWELL</a>

      <h1 style={{ fontSize: 24, fontWeight: 600, color: B.text, margin: "20px 0 8px", fontFamily: mono, lineHeight: 1.4 }}>
        How Fynwell Uses Claude to Turn Household Financial Data Into Actionable Intelligence
      </h1>
      <p style={{ fontSize: 13, color: B.muted, marginBottom: 40, fontFamily: mono }}>Case study -- March 2026</p>

      {/* Section 1: The Problem */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>The Problem</h2>
        <p style={pStyle}>
          Dual-income households accumulate financial complexity faster than they can manage it -- idle cash sitting in checking accounts, subscriptions renewing unnoticed, no single view of net worth trajectory. Existing tools show data. They don't tell you what to do.
        </p>
        <p style={pStyle}>
          The gap isn't information. It's intelligence. Households need a system that sees across every account, detects what matters, and delivers a clear action -- in the right voice, at the right time.
        </p>
      </div>

      {/* Section 2: How Claude Is Used */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>How Claude Is Used</h2>
        <ul style={{ listStyle: "none", padding: 0 }}>
          <li style={liStyle}>
            <span style={strongStyle}>Context assembly</span> -- Claude receives structured financial packets (balances, transaction signals, merchant categories, cash flow patterns) and generates prioritized, narrative-driven insights
          </li>
          <li style={liStyle}>
            <span style={strongStyle}>Insight generation</span> -- Cash drag detection, subscription audit, net worth benchmarking -- each insight produced by Claude from deterministic Governor Engine outputs
          </li>
          <li style={liStyle}>
            <span style={strongStyle}>Narrative personalization</span> -- Claude adapts tone and framing to household archetype (e.g. dual-income tech, RSU-heavy) without ever receiving raw PII
          </li>
          <li style={liStyle}>
            <span style={strongStyle}>Privacy architecture</span> -- Claude never sees account numbers, names, or raw transactions -- only structured signal packets sanitized through a dedicated PII filter
          </li>
        </ul>
      </div>

      {/* Section 3: The Architecture */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>The Architecture</h2>
        <div style={flowBoxStyle}>
          <div>Plaid API</div>
          <div style={{ color: B.muted }}>{"  ↓"}</div>
          <div>Transaction ingestion + signal extraction</div>
          <div style={{ color: B.muted }}>{"  ↓"}</div>
          <div>Governor Engine <span style={{ color: B.muted }}>(deterministic rules -- no LLM)</span></div>
          <div style={{ color: B.muted }}>{"  ↓"}</div>
          <div>Structured insight packet <span style={{ color: B.muted }}>(type, impact, confidence, facts[])</span></div>
          <div style={{ color: B.muted }}>{"  ↓"}</div>
          <div style={{ color: B.accent }}>Claude API <span style={{ color: B.muted }}>(narrative generation, archetype voice)</span></div>
          <div style={{ color: B.muted }}>{"  ↓"}</div>
          <div>Household dashboard + push notification</div>
        </div>
        <p style={{ ...pStyle, marginTop: 16, fontSize: 13 }}>
          The Governor Engine runs deterministic rules on financial data to detect patterns. Claude receives only the structured output packet -- never raw financial history. This separation ensures privacy by design while enabling natural-language insight delivery.
        </p>
      </div>

      {/* Section 4: Results */}
      <div style={sectionStyle}>
        <h2 style={h2Style}>Results</h2>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
          <div style={statCardStyle}>
            <div style={{ fontSize: 28, fontWeight: 600, color: B.accent, fontFamily: mono }}>
              {metrics?.avg_idle_cash ? `$${Math.round(metrics.avg_idle_cash).toLocaleString()}` : "$4,200"}
            </div>
            <div style={{ fontSize: 12, color: B.muted, marginTop: 6 }}>Median idle cash identified per household</div>
          </div>
          <div style={statCardStyle}>
            <div style={{ fontSize: 28, fontWeight: 600, color: B.accent, fontFamily: mono }}>
              {metrics?.insights_generated ? metrics.insights_generated.toLocaleString() : "--"}
            </div>
            <div style={{ fontSize: 12, color: B.muted, marginTop: 6 }}>Insights generated via Claude API</div>
          </div>
          <div style={statCardStyle}>
            <div style={{ fontSize: 28, fontWeight: 600, color: B.accent, fontFamily: mono }}>
              {metrics?.households_onboarded ?? "--"}
            </div>
            <div style={{ fontSize: 12, color: B.muted, marginTop: 6 }}>Households onboarded</div>
          </div>
        </div>
        <ul style={{ listStyle: "none", padding: 0 }}>
          <li style={liStyle}>
            <span style={strongStyle}>Cash drag detection</span> -- identifies idle checking balances vs. available HYSA yield opportunity
          </li>
          <li style={liStyle}>
            <span style={strongStyle}>Insight-to-action loop</span> -- designed for 48-hour decision turnaround from insight delivery to user action
          </li>
          <li style={liStyle}>
            <span style={strongStyle}>Zero PII exposure</span> -- Claude processes structured packets only; raw financial data never leaves the secure perimeter
          </li>
        </ul>
      </div>

      {/* Footer */}
      <div style={{ borderTop: `1px solid ${B.border}`, paddingTop: 24, marginTop: 40 }}>
        <p style={{ fontSize: 12, color: B.muted, lineHeight: 1.6 }}>
          Fynwell is the household intelligence layer for dual-income families. Built with{" "}
          <a href="https://www.anthropic.com/claude" style={{ color: B.accent, textDecoration: "none" }} target="_blank" rel="noopener noreferrer">Claude</a> by Anthropic,{" "}
          <a href="https://plaid.com" style={{ color: B.accent, textDecoration: "none" }} target="_blank" rel="noopener noreferrer">Plaid</a>, and{" "}
          <a href="https://supabase.com" style={{ color: B.accent, textDecoration: "none" }} target="_blank" rel="noopener noreferrer">Supabase</a>.
        </p>
        <a href="/" style={{ fontSize: 12, color: B.accent, textDecoration: "none", fontFamily: mono, display: "inline-block", marginTop: 12 }}>
          ← Back to Fynwell
        </a>
      </div>
    </div>
  );
}
